/**
 * src/components/timeslotpicker.jsx 
 * FINAL ESDRAS INLINE TIME SLOT PICKER
 * FEATURES: Real-time availability sync, Auto-blocking past slots, Horizontal Date Scroll
 */

import React, { useState, useEffect } from 'react';
import { format, addDays, startOfDay, addMinutes, isSameDay, isBefore, setHours, setMinutes } from 'date-fns';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const GOLD = '#B8860B';

export default function TimeSlotPicker({ stylistId, onSlotSelected }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const today = new Date();

  // 1. Listen for existing bookings for this stylist on the selected date
  useEffect(() => {
    if (!stylistId) return;

    const startOfSelected = startOfDay(selectedDate);
    const endOfSelected = addDays(startOfSelected, 1);

    const q = query(
      collection(db, 'bookings'),
      where('barberId', '==', stylistId),
      where('slot', '>=', startOfSelected),
      where('slot', '<', endOfSelected),
      where('status', '==', 'paid') // Only block confirmed payments in the Vault
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      // Map Firestore timestamps to JS milliseconds for easy comparison
      const booked = snapshot.docs.map(doc => doc.data().slot.toDate().getTime());
      setBookedSlots(booked);
    }, (error) => {
      console.error("Availability Sync Error:", error);
    });

    return () => unsubscribe();
  }, [stylistId, selectedDate]);

  // Generate next 7 days for the horizontal date picker
  const dates = Array.from({ length: 7 }, (_, i) => addDays(new Date(), i));

  // Generate 30-minute slots from 9:00 AM to 8:00 PM
  const generateSlots = (date) => {
    const slots = [];
    let current = startOfDay(date);
    current = setHours(current, 9);
    current = setMinutes(current, 0);

    // 22 slots covers 9:00 AM to 7:30 PM (Last session starts at 7:30)
    for (let i = 0; i < 22; i++) {
      slots.push(new Date(current));
      current = addMinutes(current, 30);
    }
    return slots;
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    onSlotSelected?.(slot); // Bubble up the selected time to BookingModal
  };

  return (
    <div style={{ marginTop: '0.5rem', textAlign: 'left' }}>
      <p style={labelStyle}>1. Pick a Date</p>
      
      {/* Date Selector – Horizontal Scroll */}
      <div style={dateScrollContainer} className="hide-scrollbar">
        {dates.map((date) => {
          const isSelected = isSameDay(date, selectedDate);
          const isToday = isSameDay(date, today);

          return (
            <button
              key={date.toISOString()}
              onClick={() => {
                setSelectedDate(date);
                setSelectedSlot(null); // Clear previous selection when date shifts
              }}
              style={{
                ...dateCardStyle,
                background: isSelected ? GOLD : 'rgba(255, 255, 255, 0.05)',
                color: isSelected ? 'black' : 'white',
                border: isToday && !isSelected ? `1px solid ${GOLD}` : 'none',
              }}
            >
              <div style={{ fontSize: '0.65rem', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '1px' }}>
                {format(date, 'EEE')}
              </div>
              <div style={{ fontSize: '1.1rem', fontWeight: '800' }}>
                {format(date, 'dd')}
              </div>
            </button>
          );
        })}
      </div>

      <p style={labelStyle}>2. Choose Available Time</p>

      {/* Time Slots Grid */}
      <div style={timeGridStyle}>
        {generateSlots(selectedDate).map((slot) => {
          const slotTime = slot.getTime();
          const isPast = isBefore(slot, today);
          const isBooked = bookedSlots.includes(slotTime);
          const isSelected = selectedSlot && slotTime === selectedSlot.getTime();

          // Disable if the time has already passed or if another client has paid for it
          const isDisabled = isPast || isBooked;

          return (
            <button
              key={slot.toISOString()}
              onClick={() => handleSlotSelect(slot)}
              disabled={isDisabled}
              style={{
                ...slotButtonStyle,
                background: isSelected ? GOLD : 'rgba(255, 255, 255, 0.03)',
                color: isSelected ? 'black' : (isBooked ? '#ff4d4d' : 'white'),
                opacity: isDisabled ? 0.3 : 1,
                border: isSelected ? `1px solid ${GOLD}` : '1px solid rgba(255,255,255,0.1)',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                textDecoration: isBooked ? 'line-through' : 'none'
              }}
            >
              {format(slot, 'h:mm a')}
            </button>
          );
        })}
      </div>

      {selectedSlot && (
        <div style={confirmationBox}>
          <span style={{ opacity: 0.7 }}>Securing session for: </span>
          <br />
          <strong style={{ color: GOLD, fontSize: '1rem' }}>
            {format(selectedSlot, 'MMMM do')} at {format(selectedSlot, 'h:mm a')}
          </strong>
        </div>
      )}
    </div>
  );
}

// --- STYLESHEET ---
const labelStyle = { 
  color: GOLD, 
  fontWeight: 'bold', 
  fontSize: '0.8rem', 
  textTransform: 'uppercase', 
  letterSpacing: '1px', 
  marginBottom: '12px', 
  textAlign: 'center' 
};

const dateScrollContainer = {
  display: 'flex',
  gap: '10px',
  overflowX: 'auto',
  padding: '5px 0 15px',
  WebkitOverflowScrolling: 'touch'
};

const dateCardStyle = {
  flexShrink: 0,
  padding: '12px',
  borderRadius: '16px',
  minWidth: '65px',
  textAlign: 'center',
  cursor: 'pointer',
  transition: '0.2s ease',
  fontFamily: 'Montserrat, sans-serif',
  border: 'none'
};

const timeGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '10px',
  maxHeight: '180px',
  overflowY: 'auto',
  padding: '10px',
  background: 'rgba(0,0,0,0.2)',
  borderRadius: '18px',
  border: '1px solid rgba(255,255,255,0.05)'
};

const slotButtonStyle = {
  padding: '12px 5px',
  borderRadius: '10px',
  fontSize: '0.8rem',
  fontWeight: '600',
  transition: '0.2s ease',
  outline: 'none'
};

const confirmationBox = { 
  marginTop: '20px', 
  padding: '15px', 
  background: 'rgba(184, 134, 11, 0.05)', 
  borderRadius: '15px', 
  border: `1px solid rgba(184, 134, 11, 0.2)`,
  textAlign: 'center',
  fontSize: '0.85rem',
  lineHeight: '1.5'
};
